import { useLocalStorageState } from 'ahooks';
import { useMemo } from 'react';

import { CHAT_MIN_HEIGHT, LAUNCHER_MIN_WIDTH, type Point, type Size } from './floating-chat-geometry';

/** One preference for the surface: the note chat and the block chat are the same surface, not two. */
export const FLOATING_CHAT_STORAGE_KEY = 'docuai-floating-chat:v1';

export type StoredGeometry = {
  docked: boolean;
  launcher: Point;
  size: Size;
  /**
   * Where a docked open chat sits relative to its dock launcher's top-left; null until a docked chat was
   * first opened. Only docked chats have one: a free-floating chat is placed from its `launcher`, which a
   * drag or resize records at the surface's bottom-left, instead of being offset from it.
   */
  openOffset: Point | null;
  /** True after the user resizes the open chat; docked opens then keep `size` instead of the launcher width. */
  customSize: boolean;
};

export const DEFAULT_FLOATING_CHAT_GEOMETRY: StoredGeometry = {
  docked: true,
  launcher: { x: 0, y: 0 },
  size: { width: LAUNCHER_MIN_WIDTH, height: CHAT_MIN_HEIGHT },
  openOffset: null,
  customSize: false,
};

/**
 * The persisted surface geometry. `listenStorageChange` keeps every instance (the surface hook and the
 * dock slots) in sync within the tab, so a slot never reasons from a stale copy after the hook persists.
 */
export const useStoredFloatingChatGeometry = () => {
  const [stored, setStored] = useLocalStorageState<StoredGeometry>(FLOATING_CHAT_STORAGE_KEY, {
    defaultValue: DEFAULT_FLOATING_CHAT_GEOMETRY,
    listenStorageChange: true,
  });
  const geometry = useMemo(() => normalizeStoredGeometry(stored), [stored]);

  return [geometry, setStored] as const;
};

const normalizeStoredGeometry = (value: StoredGeometry | undefined): StoredGeometry => {
  const isValid =
    !!value &&
    typeof value.docked === 'boolean' &&
    isFiniteNumber(value.launcher?.x) &&
    isFiniteNumber(value.launcher?.y) &&
    isFiniteNumber(value.size?.width) &&
    isFiniteNumber(value.size?.height);
  const source = isValid ? value : DEFAULT_FLOATING_CHAT_GEOMETRY;

  return {
    docked: source.docked,
    launcher: { x: source.launcher.x, y: source.launcher.y },
    size: { width: source.size.width, height: source.size.height },
    // An offset only means anything against the dock slot launcher; a free-floating chat is placed from
    // its launcher instead. A docked chat that was never resized is exactly as wide as its launcher and
    // sits flush with it, so a stale x from an earlier build — which would slide it sideways — is dropped.
    openOffset:
      source.docked && isFiniteNumber(source.openOffset?.x) && isFiniteNumber(source.openOffset?.y)
        ? { x: source.customSize === true ? source.openOffset.x : 0, y: source.openOffset.y }
        : null,
    customSize: source.customSize === true,
  };
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
